import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  LinearProgress,
  Box,
} from "components/mui";
import MenuItem from "@mui/material/MenuItem";
import { useNotificationProvider } from "hooks";
import { UpdateTemplate } from "api/canmail/CANmailAPI";

const NEW_FOLDER = "__new__";

/**
 * Edit a template's identity + organization: name (rename), folder, description
 * and tags. These are metadata-only edits, so they do NOT create a version-history
 * entry server-side.
 */
function TemplateDetailsModal({ open, template, folders = [], onClose, onSaved }) {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [name, setName] = useState("");
  const [folder, setFolder] = useState("");
  const [folderChoice, setFolderChoice] = useState(""); // dropdown selection
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && template) {
      setName(template.template_name || "");
      setFolder(template.folder || "");
      setFolderChoice(template.folder || "");
      setDescription(template.description || "");
      setTags(template.tags || "");
      setSaving(false);
    }
  }, [open, template]);

  const handleFolderChoice = (value) => {
    setFolderChoice(value);
    if (value === NEW_FOLDER) {
      setFolder("");
    } else {
      setFolder(value);
    }
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      notifyError("Template name is required.");
      return;
    }
    setSaving(true);
    try {
      await UpdateTemplate(template.template_id, {
        template_name: trimmedName,
        folder: folder.trim() || null,
        description: description.trim() || null,
        tags: tags.trim() || null,
      });
      notifySuccess("Template details updated.");
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("[TemplateDetailsModal] Error saving details:", error);
      notifyError("Could not update the template details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const showNewFolderField = folderChoice === NEW_FOLDER || folders.length === 0;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Template Details</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        <TextField
          label="Name"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 255 }}
        />

        {folders.length > 0 && (
          <TextField
            select
            label="Folder"
            fullWidth
            value={folderChoice}
            onChange={(e) => handleFolderChoice(e.target.value)}
          >
            <MenuItem value="">
              <em>No folder</em>
            </MenuItem>
            {folders.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
            <MenuItem value={NEW_FOLDER}>+ New folder…</MenuItem>
          </TextField>
        )}

        {showNewFolderField && (
          <TextField
            label="Folder"
            placeholder="e.g. Newsletters"
            fullWidth
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            inputProps={{ maxLength: 100 }}
            helperText="Group related templates together. Leave blank for no folder."
          />
        )}

        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: 500 }}
        />

        <TextField
          label="Tags"
          fullWidth
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText="Comma-separated, e.g. promo, monthly"
        />

        <Box sx={{ height: 4 }}>{saving && <LinearProgress />}</Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TemplateDetailsModal;
